import React from 'react';
import * as R from 'ramda';
import FileSaver from 'file-saver';
import { connect } from 'react-redux';
import { pure, compose, withProps, withState, withHandlers } from 'react-recompose';
// components
import { Table } from '../table';
import { FormFooter } from '../form-footer';
import { openModal, closeModal } from '../modal/actions';
import { withAsyncDocumentPreviewDrawer } from '../../components/document-preview/preview-drawer';
// features
import { exportFactoringDocument } from '../../features/master-invoice/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// hocs
import { withFixedPopover, withConnectModalAndLoaderActions, withComponentDidUpdatePropCallback } from '../../hocs';
// ui
import { Box, Flex } from '../../ui';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// components async-documents
import SendDocuments from './send-documents';
import { getReport, tableSettings, tableSettings2, getColumnSettings } from './settings';
//////////////////////////////////////////////////

const lightGreyColor = G.getTheme('colors.lightGrey');

const actionsMap = {
  [GC.DOCUMENTS_TYPE_DRIVER_PAYROLL]: {
    remove: endpointsMap.driverPayrollDocument,
    sendViaEmail: endpointsMap.driverPayrollSendViaEmail,
    download: endpointsMap.driverPayrollDownloadDocument,
    enableOnMobile: endpointsMap.driverPayrollDocumentEnableOnMobile,
    disableOnMobile: endpointsMap.driverPayrollDocumentDisableOnMobile
  },
  [GC.DOCUMENTS_TYPE_VENDOR_PAYROLL]: {
    remove: endpointsMap.vendorPayrollDocument,
    sendViaEmail: endpointsMap.vendorPayrollSendViaEmail,
    download: endpointsMap.vendorPayrollDownloadDocument,
  },
  [GC.DOCUMENTS_TYPE_MASTER_INVOICE]: {
    remove: endpointsMap.customerMasterInvoiceDocument,
    downloadZip: endpointsMap.customerMasterInvoiceDocumentsZip,
    download: endpointsMap.customerMasterInvoiceDownloadDocument,
  },
};

const withAsyncDocumentActions = compose(
  withConnectModalAndLoaderActions,
  withHandlers({
    removeDocuments: (props) => async (payload) => {
      const { itemList, openLoader, closeLoader, setItemList, documentsType } = props;

      openLoader();

      const guids = R.path(['guids'], payload);

      const options = {
        data: guids,
      };

      const endpoint = R.path([documentsType, 'remove'], actionsMap);

      const res = await sendRequest('delete', endpoint, options);

      const { status } = res;

      if (G.isResponseSuccess(status)) {
        const newItemList = R.reject((item) => R.includes(R.prop(GC.FIELD_GUID, item), guids), itemList);
        setItemList(newItemList);
      } else {
        G.handleFailResponseSimple(res);
      }

      closeLoader();
    },
    downloadDocumentsZip: ({ documentsType }) => async ({ data, setSubmitting }) => {
      const options = {
        data,
        resType: 'arraybuffer',
      };

      const endpoint = R.path([documentsType, 'downloadZip'], actionsMap);

      const res = await sendRequest('post', endpoint, options);

      const { status } = res;

      if (G.isResponseSuccess(status)) {
        G.saveFileFromResponse(res, `${documentsType}_zip`);
      } else {
        G.handleFailResponseSimple(res);
      }

      setSubmitting(false);
    },
    sendDocumentViaEmailRequest: (props) => async (options) => {
      const {
        itemList,
        openLoader,
        closeModal,
        closeLoader,
        setItemList,
        documentsType,
      } = props;

      const { mailTo, payrollGuid } = options;

      openLoader();

      const endpoint = R.path([documentsType, 'sendViaEmail'], actionsMap);

      const res = await sendRequest('post', endpoint, { data: options });

      const { status } = res;

      if (G.isResponseSuccess(status)) {
        const index = R.findIndex(R.propEq(payrollGuid, GC.FIELD_PRIMARY_OBJECT_GUID), itemList);
        const newItemList = R.assocPath([index, GC.FIELD_DOCUMENT_SENT_TO], mailTo, itemList);

        closeModal();
        setItemList(newItemList);
      } else {
        G.handleFailResponseSimple(res);
      }

      closeLoader();
    },
  }),
  pure,
);

const enhanceDocuments = compose(
  withFixedPopover,
  withState('submitting', 'setSubmitting', false),
  withState('itemList', 'setItemList', ({ data }) => R.map(R.assoc('selected', false), data)),
  withHandlers({
    handleUpdateItemListOnDataChange: (props) => () => {
      const { data, setItemList } = props;

      setItemList(R.map(R.assoc('selected', false), data));
    },
  }),
  withComponentDidUpdatePropCallback({ propName: 'data', callbackName: 'handleUpdateItemListOnDataChange' }),
  withAsyncDocumentActions,
  withProps(({ documentsType }) => ({
    getDownloadDocumentEndpoint: () => R.path([documentsType, 'download'], actionsMap),
  })),
  withAsyncDocumentPreviewDrawer,
  withHandlers({
    handleSelectItem: (props) => (data) => {
      const { itemList, setItemList } = props;

      if (R.equals(data, 'all')) {
        const value = R.not(R.all(
          (item) => item.selected,
          itemList,
        ));

        return setItemList(R.map(
          (item) => R.assoc('selected', value, item),
          itemList,
        ));
      }

      return setItemList(R.map(
        (item) => {
          if (R.propEq(data, GC.FIELD_GUID, item)) {
            return R.assoc('selected', R.not(item.selected), item);
          }

          return item;
        },
        itemList,
      ));
    },
    handleDownloadZip: (props) => () => {
      const { itemList, setSubmitting, downloadDocumentsZip } = props;

      const selectedList = R.compose(
        R.map(R.prop(GC.FIELD_GUID)),
        R.filter(R.prop('selected')),
      )(itemList);

      if (R.propEq(0, 'length', selectedList)) {
        return G.showToastrMessageSimple(
          'info',
          G.getWindowLocale('messages:select-item', 'Please, select at least one item'),
        );
      }

      setSubmitting(true);

      downloadDocumentsZip({ setSubmitting, data: selectedList });
    },
    handleRemoveDocuments: ({ itemList, removeDocuments }) => () => {
      const selectedList = R.compose(
        R.map(R.prop(GC.FIELD_GUID)),
        R.filter(R.prop('selected')),
      )(itemList);

      if (R.propEq(0, 'length', selectedList)) {
        return G.showToastrMessageSimple(
          'info',
          G.getWindowLocale('messages:select-item', 'Please, select at least one item'),
        );
      }

      removeDocuments({ guids: selectedList });
    },
    handleEnableOrDisableOnMobile: (props) => async (enable) => {
      const {
        itemList,
        openLoader,
        closeLoader,
        getDocuments,
        documentsType,
        asyncEndpoint,
      } = props;

      openLoader();

      const selectedList = R.compose(
        R.map(G.getGuidFromObject),
        R.filter(R.prop('selected')),
      )(itemList);

      const endpoint = R.path([documentsType, enable ? 'enableOnMobile' : 'disableOnMobile'], actionsMap);

      const options = {
        data: selectedList,
      };

      const res = await sendRequest('put', endpoint, options);

      const { status } = res;

      if (G.isResponseSuccess(status)) {
        const options = {
          params: { [GC.FIELD_PRIMARY_OBJECT_GUID]: R.path([0, GC.FIELD_PRIMARY_OBJECT_GUID], itemList) },
        };

        getDocuments(asyncEndpoint, options);
      } else {
        G.handleFailResponseSimple(res);
      }

      closeLoader();
    },
    handleRemoveDocument: ({ removeDocuments }) => (documentGuid) => {
      const guids = R.of(Array, documentGuid);

      removeDocuments({ guids });
    },
    handleDownloadDocument: ({ documentsType }) => async ({ params }) => {
      const options = {
        params,
        resType: 'arraybuffer',
      };

      const endpoint = R.path([documentsType, 'download'], actionsMap);

      const res = await sendRequest('get', endpoint, options);

      const { data, status, headers } = res;

      if (G.isResponseSuccess(status)) {
        const file = new window.Blob(
          R.of(Array, data),
          { type: R.pathOr('application/pdf', ['content-type'], headers) },
        );

        FileSaver.saveAs(file, params[GC.FIELD_FILE_NAME]);
      } else {
        G.handleFailResponseSimple(res);
      }
    },
    handleSendDocumentsViaEmail: (props) => () => {
      const {
        itemList,
        openModal,
        payrollGuid,
        documentsType,
        payrollTypeGuid,
        sendDocumentViaEmailRequest,
      } = props;

      const documentGuids = R.compose(
        R.map(G.getGuidFromObject),
        R.filter(R.prop('selected')),
      )(R.or(itemList, []));

      if (R.isEmpty(documentGuids)) {
        return G.showToastrMessageSimple(
          'info',
          G.getWindowLocale('messages:select-item', 'Please, select at least one item'),
        );
      }

      const isDriver = R.equals(documentsType, GC.DOCUMENTS_TYPE_DRIVER_PAYROLL);

      const submitAction = (mailTo) =>
        sendDocumentViaEmailRequest({ mailTo, payrollGuid, documentGuids });

      const component = (
        <SendDocuments
          isDriver={isDriver}
          submitAction={submitAction}
          payrollTypeGuid={payrollTypeGuid}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          title: G.getWindowLocale('titles:send-emails', 'Send Emails'),
        },
      };

      openModal(modal);
    },
    handleSendDocumentViaEmail: (props) => (documentGuid) => {
      const {
        openModal,
        payrollGuid,
        documentsType,
        payrollTypeGuid,
        sendDocumentViaEmailRequest,
      } = props;

      const documentGuids = R.of(Array, documentGuid);

      const isDriver = R.equals(documentsType, GC.DOCUMENTS_TYPE_DRIVER_PAYROLL);

      const submitAction = (mailTo) =>
        sendDocumentViaEmailRequest({ mailTo, payrollGuid, documentGuids });

      const component = (
        <SendDocuments
          isDriver={isDriver}
          submitAction={submitAction}
          payrollTypeGuid={payrollTypeGuid}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          title: G.getWindowLocale('titles:send-emails', 'Send Emails'),
        },
      };

      openModal(modal);
    },
  }),
  pure,
);

const DocumentTable = (props) => {
  const {
    version,
    loading,
    itemList,
    getDocuments,
    documentsType,
    asyncEndpoint,
    handleSelectItem,
    columnSettingsProps,
    handleOpenFilePreview,
    handleDownloadDocument,
    exportFactoringDocument,
  } = props;

  const offset = itemList.length;

  const integrationAvailable = R.any(
    (doc) => {
      const type = R.prop(GC.FIELD_INTEGRATION_TYPE, doc);

      return R.and(
        G.isNotNilAndNotEmpty(type),
        G.notEquals(type, GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL_MANUAL),
      );
    },
    itemList,
  );

  const columnSettings = getColumnSettings({
    getDocuments,
    asyncEndpoint,
    columnSettingsProps,
    exportFactoringDocument,
    download: handleDownloadDocument,
    previewDocument: handleOpenFilePreview,
  });

  const allChecked = G.isAllChecked(itemList);

  const tableSettingsTuUse = R.equals(version, 2) ? tableSettings2 : tableSettings;

  const data = {
    loading,
    allChecked,
    pagination: {
      offset,
      limit: offset,
    },
    columnSettings,
    totalCount: offset,
    hasSelectable: false,
    onOptionClick: handleSelectItem,
    tableSettings: tableSettingsTuUse,
    itemList: G.ifElse(loading, [], itemList),
    report: getReport(documentsType, integrationAvailable),
  };

  return <Table {...data} />;
};

const DocumentList = enhanceDocuments((props) => {
  const {
    version,
    itemList,
    submitting,
    wrapperStyles,
    documentsType,
    cancelBtnStyles,
    handleDownloadZip,
    mailIntegrationType,
    handleRemoveDocuments,
    handleSendDocumentsViaEmail,
    handleEnableOrDisableOnMobile,
  } = props;

  const disabled = R.all(
    R.propEq(false, 'selected'),
    itemList,
  );

  let actionButtons = [
    {
      disabled,
      action: handleRemoveDocuments,
      displayText: G.getWindowLocale('actions:remove', 'Remove'),
    },
  ];

  if (R.and(
    G.isNotNilAndNotEmpty(mailIntegrationType),
    R.includes(documentsType, [GC.DOCUMENTS_TYPE_DRIVER_PAYROLL, GC.DOCUMENTS_TYPE_VENDOR_PAYROLL]),
  )) {
    actionButtons = R.prepend(
      {
        disabled,
        action: handleSendDocumentsViaEmail,
        displayText: G.getWindowLocale('actions:send-documents', 'Send Documents'),
        buttonStyles: {
          mr: 20,
          width: 'auto',
          ml: G.ifElse(R.equals(documentsType, GC.DOCUMENTS_TYPE_DRIVER_PAYROLL), '', 'auto'),
        },
      },
      actionButtons,
    );
  }

  if (R.equals(documentsType, GC.DOCUMENTS_TYPE_DRIVER_PAYROLL)) {
    actionButtons = [
      {
        disabled,
        action: () => handleEnableOrDisableOnMobile(true),
        buttonStyles: { mr: 20, ml: 'auto', width: 'auto' },
        displayText: G.getWindowLocale('actions:show-on-mobile', 'Show On Mobile'),
      },
      {
        disabled,
        buttonStyles: { mr: 20, width: 'auto' },
        action: () => handleEnableOrDisableOnMobile(false),
        displayText: G.getWindowLocale('actions:hide-on-mobile', 'Hide On Mobile'),
      },
      ...actionButtons,
    ];
  }

  const footerBoxStyles = R.equals(version, 2) ? { p: 15, borderTop: `1px solid ${lightGreyColor}` } : { p: 15, };

  return (
    <Flex flexDirection='column' {...G.spreadUiStyles(wrapperStyles)}>
      <Box maxWidth='95vw'><DocumentTable {...props} /></Box>
      <FormFooter
        boxStyles={footerBoxStyles}
        submitDisabled={submitting}
        actionButtons={actionButtons}
        submitAction={handleDownloadZip}
        cancelBtnStyles={cancelBtnStyles}
        submitBtnStyles={
          G.ifElse(
            R.equals(documentsType, GC.DOCUMENTS_TYPE_MASTER_INVOICE),
            { width: 125 },
            { display: 'none' },
          )
        }
        submitBtnText={
          G.ifElse(
            submitting,
            G.getWindowLocale('titles:loading', 'Loading...'),
            G.getWindowLocale('titles:download-zip', 'Download ZIP'),
          )
        }
      />
    </Flex>
  );
});

export default connect(null, {
  openModal,
  closeModal,
  exportFactoringDocument,
})(DocumentList);

export {
  enhanceDocuments,
};
